import React, { useState } from 'react';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemIcon, 
  ListItemText, 
  Toolbar,
  Divider,
  Box,
  Tooltip,
  Collapse,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ScienceIcon from '@mui/icons-material/Science';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import RouterIcon from '@mui/icons-material/Router';
import AddIcon from '@mui/icons-material/Add';
import CircleIcon from '@mui/icons-material/Circle';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useSondeContext } from '../context/SondeContext';

// Largeur du tiroir de navigation
const drawerWidth = 260;

const AppNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bains: boitiers } = useSondeContext();
  const [settingsOpen, setSettingsOpen] = useState(true);
  
  // Fonction pour extraire le nom court du boîtier
  const getShortName = (boitier: any) => {
    if (boitier.name) {
      // Format: "#ID - Premier mot" (ex: "#1 - 1374")
      const firstWord = boitier.name.split(' ')[0];
      return `#${boitier.boitier_id} - ${firstWord}`;
    }
    return `Boîtier #${boitier.boitier_id}`;
  };
  
  // Navigation vers une page
  const handleNavigation = (path: string) => {
    navigate(path);
  };
  
  // Vérifier si un chemin est actif
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Route unique, portee par l'id du boitier — cf. Dashboard.
  const getDeviceRoute = (boitier: any): string => `/boitier/${boitier.boitier_id}`;

  // Trier les boîtiers par ID
  const sortedBoitiers = [...boitiers].sort((a, b) => a.boitier_id - b.boitier_id);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
      }}
    >
      <Toolbar /> {/* Espace pour tenir compte de la barre d'application */}
      <Box sx={{ overflow: 'auto' }}>
        {/* Navigation principale */}
        <List>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleNavigation('/')}
              selected={location.pathname === '/'}
            >
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Tableau de bord" />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleNavigation('/alerts')}
              selected={isActive('/alerts')}
            >
              <ListItemIcon>
                <NotificationsActiveIcon />
              </ListItemIcon>
              <ListItemText primary="Alertes" />
            </ListItemButton>
          </ListItem>
        </List>
        
        <Divider />

        {/* Section Paramètres (pliable) */}
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => setSettingsOpen(!settingsOpen)}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Paramètres" />
              {settingsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          
          <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem disablePadding>
                <ListItemButton 
                  sx={{ pl: 4 }}
                  onClick={() => handleNavigation('/settings')}
                  selected={location.pathname === '/settings'}
                >
                  <ListItemIcon>
                    <TuneIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Seuils d'alertes" 
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                  />
                </ListItemButton>
              </ListItem>
              
              <ListItem disablePadding>
                <ListItemButton 
                  sx={{ pl: 4 }}
                  onClick={() => handleNavigation('/regulation')}
                  selected={location.pathname === '/regulation'}
                >
                  <ListItemIcon>
                    <SettingsInputComponentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Régulation relais" 
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                  />
                </ListItemButton>
              </ListItem>
              
              <ListItem disablePadding>
                <ListItemButton 
                  sx={{ pl: 4 }}
                  onClick={() => handleNavigation('/admin/devices')}
                  selected={isActive('/admin/devices')}
                >
                  <ListItemIcon>
                    <RouterIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Gestion boîtiers" 
                    primaryTypographyProps={{ fontSize: '0.9rem' }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Collapse>
        </List>
        
        <Divider />
        
        {/* Liste des boîtiers */}
        <List>
          <ListItem 
            secondaryAction={
              <Tooltip title="Ajouter un boîtier">
                <ListItemButton 
                  onClick={() => handleNavigation('/admin/devices/new')}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  <AddIcon fontSize="small" />
                </ListItemButton>
              </Tooltip>
            }
          >
            <ListItemText 
              primary="Boîtiers" 
              primaryTypographyProps={{ fontWeight: 'bold' }}
              secondary={`${sortedBoitiers.length} boîtier${sortedBoitiers.length > 1 ? 's' : ''}`}
            />
          </ListItem>
          
          {sortedBoitiers.length === 0 ? (
            <ListItem>
              <ListItemText 
                secondary="Aucun boîtier configuré"
                secondaryTypographyProps={{ fontStyle: 'italic', textAlign: 'center' }}
              />
            </ListItem>
          ) : (
            sortedBoitiers.map((boitier) => (
              <ListItem key={boitier.boitier_id} disablePadding>
                <ListItemButton 
                  onClick={() => handleNavigation(getDeviceRoute(boitier))}
                  selected={isActive(getDeviceRoute(boitier))}
                >
                  <ListItemIcon>
                    <ScienceIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={getShortName(boitier)}
                    secondary={boitier.location || null}
                  />
                  {/* Indicateur de statut */}
                  <Tooltip title={boitier.online ? 'En ligne' : 'Hors ligne'}>
                    <CircleIcon 
                      sx={{ 
                        fontSize: 12, 
                        color: boitier.online ? 'success.main' : 'error.main',
                        ml: 1
                      }} 
                    />
                  </Tooltip>
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>
        
        <Divider />
        
        {/* Lien vers les codes LED */}
        <List>
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleNavigation('/codes-led')}
              selected={isActive('/codes-led')}
            >
              <ListItemIcon>
                <LightbulbIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Codes LED" 
                secondary="Guide visuel"
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
};

export default AppNavigation;
